import { useMemo } from 'react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DispositionBadge } from '@/features/dispositionBadge'
import { useList } from '@/entities/list'
import type { CrmListRow } from '@intone/shared/types'

interface DialogViewStatsProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  list: CrmListRow | null
}

export function DialogViewStats({ open, onOpenChange, list }: DialogViewStatsProps) {
  const { data, isLoading } = useList(list?.id ?? null)

  const stats = useMemo(() => {
    if (!data?.listStats) return []
    const total = data.listStats.reduce((sum, s) => sum + s.count, 0)
    return data.listStats.map((s) => ({
      ...s,
      percentage: total > 0 ? (s.count / total) * 100 : 0,
    }))
  }, [data?.listStats])

  const total = stats.reduce((sum, s) => sum + s.count, 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Stats for {list?.name || 'List'}
            <span className="text-gray-500 font-normal ml-2">({total} leads)</span>
          </DialogTitle>
        </DialogHeader>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Count</TableHead>
                <TableHead className="text-right">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : stats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                    No data
                  </TableCell>
                </TableRow>
              ) : (
                stats.map((stat) => (
                  <TableRow key={stat.status || 'null'}>
                    <TableCell>
                      <DispositionBadge
                        disposition={stat.status || 'No Status'}
                        isEditable={false}
                      />
                    </TableCell>
                    <TableCell className="text-right">{stat.count.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{stat.percentage.toFixed(1)}%</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}